'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  browserStorage,
  parsePeriodUrl,
  readStoredPeriod,
  resolveInitialSelection,
  resolveSelection,
  selectionFromPeriod,
  serializePeriodUrl,
  writeStoredPeriod,
  type AnalyticsPeriod,
  type PeriodPresetId,
  type PeriodResolution,
  type PeriodSelection,
} from '@/lib/analytics-period-state'
import { createLatestRequestGate } from '@/lib/latest-request'
import {
  fetchMarketMoneyPayouts,
  fetchMarketMoneyReport,
  moscowToday,
  type MarketMoneyPayouts,
  type MarketMoneyRange,
  type MarketMoneyReport,
} from '@/lib/market-money'

function loadErrorMessage(cause: unknown, fallback: string): string {
  const message = cause instanceof Error ? cause.message : ''
  return /network|fetch/i.test(message)
    ? 'Не удалось связаться с сервером аналитики. Проверьте подключение и обновите страницу.'
    : message || fallback
}

export interface MarketMoneyPayoutsState {
  payouts: MarketMoneyPayouts | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

export const MarketMoneyPayoutsContext = createContext<MarketMoneyPayoutsState | null>(null)

/** Fetches the range-independent payout data once for every consumer below it. */
export function MarketMoneyPayoutsProvider({ children }: { children: ReactNode }) {
  const [payouts, setPayouts] = useState<MarketMoneyPayouts | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const gate = useRef(createLatestRequestGate())

  const reload = useCallback(async () => {
    const request = gate.current.begin()
    setLoading(true)
    setError(null)
    try {
      const next = await fetchMarketMoneyPayouts()
      if (gate.current.isCurrent(request)) setPayouts(next)
    } catch (cause) {
      if (!gate.current.isCurrent(request)) return
      setPayouts(null)
      setError(loadErrorMessage(cause, 'Не удалось загрузить выплаты Маркета'))
    } finally {
      if (gate.current.isCurrent(request)) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const current = gate.current
    void reload()
    return () => current.invalidate()
  }, [reload])

  const value = useMemo(() => ({ payouts, loading, error, reload }), [payouts, loading, error, reload])
  return <MarketMoneyPayoutsContext.Provider value={value}>{children}</MarketMoneyPayoutsContext.Provider>
}

export function useMarketMoneyPayouts(): MarketMoneyPayoutsState {
  const state = useContext(MarketMoneyPayoutsContext)
  if (!state) throw new Error('useMarketMoneyPayouts must be used inside MarketMoneyPayoutsProvider')
  return state
}

export interface MarketMoneyReportState {
  report: MarketMoneyReport | null
  loading: boolean
  error: string | null
  period: AnalyticsPeriod | null
  /** A remembered preset that still waits for the first order date to resolve. */
  pendingPreset: PeriodPresetId | null
  notice: string | null
  changePeriod: (next: AnalyticsPeriod) => void
  reload: () => Promise<void>
}

export interface AnalyticsPeriodState {
  resolution: PeriodResolution
  today: string
  changePeriod: (next: AnalyticsPeriod) => void
}

/** The analytics period shared by every analytics page: URL first, then the remembered choice, then this month. */
export function useAnalyticsPeriod(): AnalyticsPeriodState {
  const [searchParams, setSearchParams] = useSearchParams()
  const { payouts, loading: payoutsLoading } = useMarketMoneyPayouts()
  const [selection, setSelection] = useState<PeriodSelection>(
    () => resolveInitialSelection(parsePeriodUrl(searchParams), readStoredPeriod(browserStorage())).selection,
  )
  const today = moscowToday()
  // The payouts presets carry the first synchronised order date used for «Всё время» and clamping.
  const firstOrderDate = payouts?.presets.paid?.from ?? null
  const firstOrderDateSettled = payouts !== null || !payoutsLoading
  const resolution = useMemo(
    () => resolveSelection(selection, { today, firstOrderDate, firstOrderDateSettled }),
    [selection, today, firstOrderDate, firstOrderDateSettled],
  )
  const changePeriod = useCallback((next: AnalyticsPeriod) => {
    const nextSelection = selectionFromPeriod(next)
    setSelection(nextSelection)
    writeStoredPeriod(browserStorage(), nextSelection)
    setSearchParams((current) => serializePeriodUrl(nextSelection, current), { replace: true })
  }, [setSearchParams])
  return { resolution, today, changePeriod }
}

/** Owns the remembered period and the range-dependent report; only the newest request may update state. */
export function useMarketMoneyReport(): MarketMoneyReportState {
  const { resolution, changePeriod } = useAnalyticsPeriod()
  const [report, setReport] = useState<MarketMoneyReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const gate = useRef(createLatestRequestGate())
  const requestedKey = useRef<string | null>(null)
  const lastRange = useRef<MarketMoneyRange | null>(null)

  const load = useCallback(async (range: MarketMoneyRange) => {
    lastRange.current = range
    const sequence = gate.current.begin()
    setLoading(true)
    setError(null)
    try {
      const next = await fetchMarketMoneyReport(range)
      if (gate.current.isCurrent(sequence)) setReport(next)
    } catch (cause) {
      if (!gate.current.isCurrent(sequence)) return
      setReport(null)
      setError(loadErrorMessage(cause, 'Не удалось загрузить продажи и выплаты Маркета'))
    } finally {
      if (gate.current.isCurrent(sequence)) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (resolution.status !== 'ready') return
    const { range } = resolution.period
    const key = `${range.from}:${range.to}`
    if (requestedKey.current === key) return
    requestedKey.current = key
    void load(range)
  }, [resolution, load])

  useEffect(() => {
    const current = gate.current
    return () => {
      current.invalidate()
      // A remount (StrictMode replays effects) must request again, or the invalidated response is never replaced.
      requestedKey.current = null
    }
  }, [])

  const reload = useCallback(async () => {
    if (lastRange.current) await load(lastRange.current)
  }, [load])

  return {
    report,
    loading,
    error,
    period: resolution.status === 'ready' ? resolution.period : null,
    pendingPreset: resolution.status === 'pending' ? resolution.preset : null,
    notice: resolution.status === 'ready' ? resolution.notice : null,
    changePeriod,
    reload,
  }
}
